import React, { useRef } from 'react';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Link } from '@mui/material';
import DOMPurify from 'dompurify';

import { MDBox, MDButton, MDInput, MDTypography } from '@components/index';
import { RootState } from '@store/store';
import { getCommentLocalTime } from '@utils/common';
import { ChartComment } from '@DStypes/chart';
import colors from '@theme/base/colors';
import rgba from '@theme/functions/rgba';
import UserAuth from '@services/auth';
import { sendAmplitudeData } from '@utils/amplitude';
import { CLICKED_KNOW_MORE, LEAVE_CHART_COMMENT } from '@constants/eventNames';

interface Props {
  commentsList: ChartComment[];
  onPostComment: (commentDetails: any) => void;
  height?: number;
  isChartPage?: boolean;
  isEditChartOrPB?: boolean;
}

const commentBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '85%',
  rowGap: '1rem',
  borderRadius: '10px',
  padding: '8px',
  boxShadow: '0 10px 27px 0 rgb(0 0 0 / 5%)'
};

const CommentsList: FC<Props> = ({
  commentsList,
  onPostComment,
  height,
  isChartPage,
  isEditChartOrPB
}) => {
  const isLoggedIn = UserAuth.isAuthenticated;

  const [comment, setComment] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const commentsRef = useRef<HTMLDivElement | null>(null);
  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setComment(value);
    if (value.length > 1000) {
      setError(true);
      setErrorMessage('Character limit 1000 exceeded');
    }

    if (error === true && value.length <= 1000) setError(false);
  };

  const handlePostComment = () => {
    if (comment.replace(/\s/g, '').length === 0) {
      setError(true);
      setErrorMessage('Please enter a comment');

      return;
    }
    if (comment.length > 1000) {
      setError(true);

      return;
    }

    const sanitizedComment = DOMPurify.sanitize(comment);

    onPostComment({ comment: sanitizedComment });
    sendAmplitudeData(LEAVE_CHART_COMMENT);
    setComment('');
  };

  useEffect(() => {
    if (commentsList.length && commentsRef.current)
      commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
  }, [commentsList]);

  return (
    <MDBox
      width='100%'
      maxWidth='100%'
      height={{ xs: '100vh', lg: `${(height || 95) - 95}px` }}
      sx={{ paddingBottom: '16px' }}
    >
      <Grid container direction={'column'} height={'100%'} wrap={'nowrap'}>
        <Grid item>
          {!isEditChartOrPB ? (
            <>
              <MDTypography
                variant='h4'
                color='info'
                sx={{ textAlign: 'center' }}
              >
                Comments & Feedback
              </MDTypography>
              <MDBox
                sx={{
                  borderBottom: '1px solid #000',
                  paddingBottom: '10px',
                  width: '95%',
                  margin: 'auto'
                }}
              ></MDBox>
            </>
          ) : (
            <></>
          )}
        </Grid>
        <Grid
          item
          container
          flexDirection='column'
          wrap='nowrap'
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            paddingX: isEditChartOrPB ? '0px' : '16px',
            width: '100%',
            maxHeight: '62%'
          }}
          ref={commentsRef}
        >
          {commentsList.length > 0 &&
            commentsList.map((comment: any) => (
              <MDBox
                display='flex'
                flexDirection='column'
                alignItems={
                  isLoggedIn
                    ? comment.author_text === userDetails.user.email
                      ? 'flex-end'
                      : 'flex-start'
                    : comment.author_text === 'Anonymous via link'
                    ? 'flex-end'
                    : 'flex-start'
                }
                rowGap={2}
                width='100%'
                m={0}
                my={1}
                key={comment.id}
              >
                <MDBox
                  variant='gradient'
                  sx={{
                    ...commentBoxStyles,
                    backgroundColor: isLoggedIn
                      ? comment.author_text === userDetails.user.email
                        ? rgba(colors.chatColors.primary, 0.6)
                        : colors.chatColors.secondary
                      : comment.author_text === 'Anonymous via link'
                      ? rgba(colors.chatColors.primary, 0.6)
                      : colors.chatColors.secondary
                  }}
                >
                  <MDTypography
                    variant='xxs'
                    color='dark'
                    sx={{
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      fontWeight: '400'
                    }}
                  >{`${comment.author_text}  ${getCommentLocalTime(
                    comment
                  )}`}</MDTypography>
                  <MDTypography
                    variant='sm'
                    color='dark'
                    sx={{
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1rem',
                      fontWeight: '400',
                      lineBreak: 'anywhere'
                    }}
                  >
                    {comment.comment}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
        </Grid>

        {commentsList.length > 0 && (
          <Grid item>
            <MDBox
              sx={{
                borderBottom: '1px solid #000',
                paddingBottom: '10px',
                width: '95%',
                margin: 'auto'
              }}
            ></MDBox>
          </Grid>
        )}
        <Grid
          item
          minHeight={window.innerHeight < 650 && isChartPage ? '61%' : '33%'}
        >
          <MDBox display='flex' flexDirection='column'>
            <MDTypography
              variant='h6'
              color='dark'
              mb={1.5}
              mt={1}
              ml={isEditChartOrPB ? 0 : 2.5}
            >
              Post a comment for others to view when you share
            </MDTypography>
            <MDInput
              value={comment}
              onChange={handleChange}
              fullWidth
              rows={1}
              multiline
              sx={{
                paddingX: isEditChartOrPB ? 0 : 2.5,
                backgroundImage: 'none',
                marginBottom: 1
              }}
              error={error}
              helperText={error && errorMessage}
              placeholder='Be nice. Be kind. Be to the point :)'
            />
            <MDBox
              display='flex'
              justifyContent='flex-end'
              width='100%'
              pr={isEditChartOrPB ? 0 : 2}
              mb={2}
            >
              <MDButton
                color='primary'
                variant='gradient'
                onClick={handlePostComment}
                sx={{
                  padding: '0px !important'
                }}
                size='small'
              >
                <MDTypography variant='xs' color='white'>
                  POST
                </MDTypography>
              </MDButton>
            </MDBox>

            <MDTypography
              variant='sm'
              color='secondary'
              sx={{
                textAlign: 'center',
                textUnderlineOffset: 4.5
              }}
              component={Link}
              href='https://datasquirrel.notion.site/More-about-commenting-22ea516314a74ce489d6fab188498dc8'
              target='_blank'
              rel='noreferrer'
              underline
              fontWeight='medium'
              textGradient
              marginTop={commentsList.length > 0 ? '1rem' : '0px'}
            >
              <span
                role='presentation'
                onClick={() =>
                  sendAmplitudeData(CLICKED_KNOW_MORE, {
                    'Feature Name': 'Comment'
                  })
                }
              >
                Want to know more about Commenting?
              </span>
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default CommentsList;
